/**
 * Call audio & haptics manager.
 *
 * Two independent streams:
 *
 *   1. CALLEE — while an incoming call is ringing, the callee's device
 *      loops `/sounds/allure_ringtone.ogg` (same file that ships as
 *      `res/raw/allure_ringtone.ogg` on the native side, so the tone
 *      is identical whether the OS rings via the lock-screen channel
 *      or the WebView rings via HTML5 audio). Every note attack in the
 *      melody triggers a haptic impact so users FEEL the ringtone
 *      when the phone is face-down or in a bag. Onset timings were
 *      derived offline via spectral-flux onset detection on the audio
 *      (see PRD 2026-09-06 entry); the array below is the ground
 *      truth — DO NOT tweak without re-running the analysis, the
 *      alignment is what makes the haptics feel like part of the tone
 *      instead of a generic buzz.
 *
 *   2. CALLER — while the outgoing call rings (before the callee
 *      picks up), the caller hears a synthesized ringback tone via
 *      Web Audio. Euro-standard cadence (425Hz sine wave, 1s on /
 *      3s off) at low volume. Synthesized rather than bundled to
 *      avoid another audio asset — a pure sine at 425Hz is 20 bytes
 *      of code and sounds identical to every desk phone in Europe.
 *
 * Both streams stop cleanly on any state exit (accept, decline,
 * end, unmount) via {@link stopAll}.
 */

// Onset timings within the 9.14s Allure ringtone (seconds from start).
// Derived from spectral-flux onset detection over the trimmed OGG.
// Loop period matches the audio duration exactly so haptics never drift.
const RINGTONE_ONSETS = [
  2.032, 2.287, 3.030, 3.715, 4.029, 4.574, 5.317,
  6.002, 6.293, 6.861, 7.419, 7.570, 8.313, 8.568, 8.858,
];
const RINGTONE_DURATION_MS = 9140;
const RINGTONE_URL = '/sounds/allure_ringtone.ogg';

// Ringback tone (caller side). Euro cadence: 1s ring, 3s silence.
const RINGBACK_FREQ_HZ = 425;
const RINGBACK_ON_MS = 1000;
const RINGBACK_OFF_MS = 3000;
const RINGBACK_VOLUME = 0.14;

// -- CALLEE ringtone + haptics ------------------------------------------

let ringtoneAudio = null;
let hapticTimeouts = [];
let hapticLoopHandle = null;
let hapticsPluginPromise = null;

const loadHapticsPlugin = () => {
  if (hapticsPluginPromise) return hapticsPluginPromise;
  // Dynamic import so the web bundle doesn't fail if @capacitor/haptics
  // isn't installed in the environment. Silently falls back to
  // navigator.vibrate() below.
  hapticsPluginPromise = import('@capacitor/haptics')
    .then((mod) => ({ Haptics: mod.Haptics, ImpactStyle: mod.ImpactStyle }))
    .catch(() => null);
  return hapticsPluginPromise;
};

const fireHapticPulse = async (strong = false) => {
  try {
    const plugin = await loadHapticsPlugin();
    if (plugin?.Haptics && plugin?.ImpactStyle) {
      // Medium impact on melodic downbeats, Light on grace notes so the
      // haptic rhythm has dynamics (matches the tone's musical accents).
      await plugin.Haptics.impact({
        style: strong ? plugin.ImpactStyle.Medium : plugin.ImpactStyle.Light,
      });
      return;
    }
  } catch { /* noop — fall through to web vibrate */ }

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(strong ? 45 : 25); } catch { /* noop */ }
  }
};

const scheduleHapticLoop = () => {
  clearHaptics();
  RINGTONE_ONSETS.forEach((t, index) => {
    // Downbeats every ~500ms → Medium; closer intervals (grace notes) → Light.
    const prev = index > 0 ? RINGTONE_ONSETS[index - 1] : -1;
    const isStrong = (t - prev) > 0.3;
    const handle = setTimeout(() => fireHapticPulse(isStrong), Math.round(t * 1000));
    hapticTimeouts.push(handle);
  });
  hapticLoopHandle = setTimeout(scheduleHapticLoop, RINGTONE_DURATION_MS);
};

const clearHaptics = () => {
  hapticTimeouts.forEach((h) => clearTimeout(h));
  hapticTimeouts = [];
  if (hapticLoopHandle) {
    clearTimeout(hapticLoopHandle);
    hapticLoopHandle = null;
  }
};

export const startRingtone = async () => {
  stopRingtone();
  try {
    ringtoneAudio = new Audio(RINGTONE_URL);
    ringtoneAudio.loop = true;
    ringtoneAudio.volume = 1.0;
    // preload metadata so the first ring isn't delayed by fetch
    ringtoneAudio.preload = 'auto';
    await ringtoneAudio.play().catch(() => { /* autoplay may be blocked
      pre-gesture; silent fail so the call flow still opens */ });
    scheduleHapticLoop();
  } catch { /* noop */ }
};

export const stopRingtone = () => {
  clearHaptics();
  if (ringtoneAudio) {
    try {
      ringtoneAudio.pause();
      ringtoneAudio.currentTime = 0;
    } catch { /* noop */ }
    ringtoneAudio = null;
  }
};

// -- CALLER ringback (synthesized) --------------------------------------

let ringbackCtx = null;
let ringbackGain = null;
let ringbackOsc = null;
let ringbackCycle = null;

const stopRingbackTick = () => {
  try { ringbackOsc?.stop(); } catch { /* noop */ }
  ringbackOsc = null;
};

export const startRingback = async () => {
  stopRingback();
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    ringbackCtx = new AudioCtx();
    if (ringbackCtx.state === 'suspended') {
      await ringbackCtx.resume().catch(() => {});
    }
    ringbackGain = ringbackCtx.createGain();
    ringbackGain.gain.value = 0;
    ringbackGain.connect(ringbackCtx.destination);

    const tick = () => {
      if (!ringbackCtx || !ringbackGain) return;
      stopRingbackTick();
      const osc = ringbackCtx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = RINGBACK_FREQ_HZ;
      osc.connect(ringbackGain);
      // Envelope: 30ms ramp up, hold, 30ms ramp down — avoids audible clicks.
      const now = ringbackCtx.currentTime;
      const holdEnd = now + RINGBACK_ON_MS / 1000;
      ringbackGain.gain.cancelScheduledValues(now);
      ringbackGain.gain.setValueAtTime(0, now);
      ringbackGain.gain.linearRampToValueAtTime(RINGBACK_VOLUME, now + 0.03);
      ringbackGain.gain.setValueAtTime(RINGBACK_VOLUME, holdEnd - 0.03);
      ringbackGain.gain.linearRampToValueAtTime(0, holdEnd);
      osc.start(now);
      osc.stop(holdEnd + 0.02);
      ringbackOsc = osc;
      ringbackCycle = setTimeout(tick, RINGBACK_ON_MS + RINGBACK_OFF_MS);
    };
    tick();
  } catch { /* noop */ }
};

export const stopRingback = () => {
  if (ringbackCycle) {
    clearTimeout(ringbackCycle);
    ringbackCycle = null;
  }
  stopRingbackTick();
  try { ringbackGain?.disconnect(); } catch { /* noop */ }
  ringbackGain = null;
  try { ringbackCtx?.close(); } catch { /* noop */ }
  ringbackCtx = null;
};

export const stopAllCallAudio = () => {
  stopRingtone();
  stopRingback();
};
